import React, { useState, useEffect } from 'react';
import {
	GetUploadEndpoint,
	MakeItemPublic,
	UploadToS3,
} from '../../../../connection/uploads';
import { GetDefaultPasscodeList } from '../../../../store/utils/create-event';
import {
	PASSCODE_LIST_TYPE,
	PasscodeList,
} from '../../../../types/working-model';
import GenerateExampleCsv from '../../../../utils/generate-csv';
import LargeButton from '../../../general-ui/button/large-button';
import RadioGroup from '../../../general-ui/radio-group/radio-group';
import TagSelectInput from '../../../general-ui/tag-select/tag-select';
import TextInput, { Validation } from '../../../general-ui/text-input/text';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../../store/types';
import { SelectOption } from '../../../general-ui/select/select';

interface PasscodeListProps {
	onChange: (lists: PasscodeList[]) => void;
	onCreate: (list: WorkingPasscodeList | undefined) => void;
	initialPasscodeLists?: PasscodeList[];
	nameErrorMessage?: string;
	urlErrorMessage?: string;
}

export interface WorkingPasscodeList {
	type: PASSCODE_LIST_TYPE;
	name: string;
	max_uses: number;
	master_passcode: string;
	url: string;
}

const passcodeListTypeOptions: SelectOption[] = [
	{
		label: 'Email only',
		value: PASSCODE_LIST_TYPE.EMAILS_ONLY.toString(),
	},
	{
		label: 'Passcode only',
		value: PASSCODE_LIST_TYPE.PASSCODES_ONLY.toString(),
	},
	{
		label: 'Both email and passcode',
		value: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES.toString(),
	},
];

export default function PasscodeListCreator(
	props: PasscodeListProps
): JSX.Element {
	const { onChange, onCreate, initialPasscodeLists, nameErrorMessage, urlErrorMessage } = props;
	const {
		AuthReducer: { user, token },
		PasscodeListReducer: { passcodeLists },
	} = useSelector((state: EventsState) => state);
	const [creatingNewPasscodeList, setCreatingNewPasscodeList] = useState(
		initialPasscodeLists
			? false
			: true
	);
	const [workingPasscodeList, setWorkingPasscodeList] = useState<
		WorkingPasscodeList
	>(GetDefaultPasscodeList());
	const [selectedPasscodeLists, setSelectedPasscodeLists] = useState<
		PasscodeList[] | null
	>(initialPasscodeLists?.length ? initialPasscodeLists : null);

	async function onFile(file: File | FileList) {
		if (file instanceof File) {
			console.log("uploading file onFile");
			const endpoint = await GetUploadEndpoint(
				token!,
				`${user!.active_channel}/${uuid()}`,
				file.name.replace(/[^0-9a-zA-Z.]/g, '-')
			);

			await UploadToS3(
				endpoint.url,
				file,
				endpoint.fields
			);
			await MakeItemPublic(token!, endpoint.fields.Key);

			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: endpoint.url + '/' + endpoint.fields.Key,
			}));
		}
	}

	function handlePasscodeListType(value: string | number) {
		switch (value) {
			case PASSCODE_LIST_TYPE.EMAILS_ONLY.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.PASSCODES_ONLY.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.PASSCODES_ONLY,
				}));
				break;
			}
			case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES.toString(): {
				setWorkingPasscodeList((list: WorkingPasscodeList) => ({
					...list,
					type: PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
				}));
				break;
			}
		}
	}

	function handlePasscodeListName(e: React.ChangeEvent<HTMLInputElement>) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			name: e.target.value,
		}));
	}

	function handlePasscodeListMasterPasscode(
		e: React.ChangeEvent<HTMLInputElement>
	) {
		setWorkingPasscodeList((list: WorkingPasscodeList) => ({
			...list,
			master_passcode: e.target.value,
		}));
	}

	// commenting out instead of deleting because passcodes is under active development
	// function handlePasscodeListMaxUses(e: React.ChangeEvent<HTMLInputElement>) {
	// 	setWorkingPasscodeList((list: WorkingPasscodeList) => ({
	// 		...list,
	// 		max_uses: Number(e.target.value),
	// 	}));
	// }

	function selectPasscodeList(tags: string[]) {
		const listsByName: { [key: string]: PasscodeList } = {};

		for (const list of passcodeLists) {
			listsByName[list.name] = list;
		}

		setSelectedPasscodeLists(tags.map((tag: string) => listsByName[tag]));
	}

	useEffect(() => {
		if (creatingNewPasscodeList) {
			onCreate(workingPasscodeList);
		} else {
			onChange(selectedPasscodeLists ?? []);
			onCreate(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPasscodeLists, workingPasscodeList, creatingNewPasscodeList]);

	const options: string[] = Array.from(
		new Set(passcodeLists?.map((list: PasscodeList) => list.name))
	);
	const initialTags: string[] = initialPasscodeLists?.map((list) => list.name) || [];

	return (
		<div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: 16,
				}}
			>
				<RadioGroup
					options={[
						{
							label: 'Uploading List',
							value: 'uploading',
						},
						{
							label: 'Connect a list',
							value: 'select',
						},
					]}
					onChange={(value: string | number) => {
						setCreatingNewPasscodeList(value === 'uploading');
					}}
					selected={creatingNewPasscodeList ? 'uploading' : 'select'}
					style={{ marginTop: 0 }}
				/>
				<button className="primary small" onClick={GenerateExampleCsv}>
					Download Example
				</button>
			</div>

			{creatingNewPasscodeList ? (
				<div style={{ marginTop: 16, marginBottom: 16 }}>
					<LargeButton
						title={'Upload a passcode list'}
						subtitle={'or drag and drop here in csv file format'}
						onFile={onFile}
						allowedFileTypes={[
							'text/csv',
							'application/vnd.ms-excel',
						]}
						errorMessage={urlErrorMessage}
					/>
					<div style={{ marginTop: 16 }}>
						<TextInput
							label={'Passcode list name*'}
							onChange={handlePasscodeListName}
							placeholder={'Passcode list name'}
							errorMessage={nameErrorMessage}
							valid={nameErrorMessage ? Validation.error : Validation.normal}
						/>
						<TextInput
							label={'Master Passcode'}
							onChange={handlePasscodeListMasterPasscode}
							placeholder={'Enter master passcode'}
						/>
						<RadioGroup
							options={passcodeListTypeOptions}
							onChange={handlePasscodeListType}
							selected={workingPasscodeList?.type.toString() ?? 0}
							style={{ marginTop: 0 }}
						/>
					</div>
				</div>
			) : (
				<div style={{ marginTop: 16 }}>
					<TagSelectInput
						onChange={selectPasscodeList}
						defaultTags={options}
						label={'Passcode lists'}
						shouldSearch={true}
						initialTags={initialTags}
					/>
				</div>
			)}
		</div>
	);
}
